import {
  LoginResponse,
  LoginResponseData,
} from '../../pages/login-page/login-model';
import { Injectable } from '@angular/core';
import { ApiServiceService } from './api-service.service';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';

export type AuthenticatedUser = {
  name: string;
  email: string;
  username: string;
  roles: string[];
  permissions: string[];
  userId: string;
  accountType: string;
  isTotpEnabled: boolean;
  mdaId: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly rememberedLoginEmailKey = 'rememberedLoginEmail';
  private readonly authenticatedUserKey = 'authenticatedUser';
  private readonly userPermissionsKey = 'userPermissions';
  private readonly userRolesKey = 'userRoles';
  private authStatus$ = new BehaviorSubject<boolean | null>(null);

  constructor(private apiService: ApiServiceService) {}

  saveRememberedLoginEmail(email: string): void {
    localStorage.setItem(this.rememberedLoginEmailKey, email);
  }

  getRememberedLoginEmail(): string {
    return localStorage.getItem(this.rememberedLoginEmailKey) ?? '';
  }

  getAccountType(): string {
    return this.getAuthenticatedUser()?.accountType ?? '';
  }

  clearRememberedLoginEmail(): void {
    localStorage.removeItem(this.rememberedLoginEmailKey);
  }

  saveAuthenticatedSession(response: LoginResponse): void {
    this.saveAuthenticatedUserData(response.data);
  }

  saveAuthenticatedUserData(data: LoginResponseData): void {
    const authenticatedUser: AuthenticatedUser = {
      name: data.name,
      email: data.email,
      username: data.username,
      roles: data.roles,
      permissions: data.permissions,
      userId: data.userId,
      accountType: data.account_type,
      isTotpEnabled: data.is_totp_enabled ?? false,
      mdaId: data.mdaId ?? null,
    };

    sessionStorage.setItem(
      this.authenticatedUserKey,
      JSON.stringify(authenticatedUser),
    );
    sessionStorage.setItem(
      this.userPermissionsKey,
      JSON.stringify(data.permissions),
    );
    sessionStorage.setItem(
      "mdaId",
      JSON.stringify(data.mdaId ?? null),
    )
    sessionStorage.setItem(this.userRolesKey, JSON.stringify(data.roles));
  }

  getAuthenticatedUser(): AuthenticatedUser | null {
    const rawUser = sessionStorage.getItem(this.authenticatedUserKey);
    if (!rawUser) return null;

    return JSON.parse(rawUser) as AuthenticatedUser;
  }

  getUserPermissions(): string[] {
    const rawPermissions = sessionStorage.getItem(this.userPermissionsKey);
    if (!rawPermissions) return [];

    return JSON.parse(rawPermissions) as string[];
  }

  getUserRoles(): string[] {
    const rawRoles = sessionStorage.getItem(this.userRolesKey);
    if (!rawRoles) return [];

    return JSON.parse(rawRoles) as string[];
  }

  isAuthenticated(): boolean {
    return !!this.getAuthenticatedUser();
  }

  isTotpEnabled(): boolean {
    return this.getAuthenticatedUser()?.isTotpEnabled ?? false;
  }

  setTotpEnabled(isTotpEnabled: boolean): void {
    const authenticatedUser = this.getAuthenticatedUser();
    if (!authenticatedUser) return;

    sessionStorage.setItem(
      this.authenticatedUserKey,
      JSON.stringify({
        ...authenticatedUser,
        isTotpEnabled,
      }),
    );
  }

  clearAuthenticatedSession(): void {
    sessionStorage.removeItem(this.authenticatedUserKey);
    sessionStorage.removeItem(this.userPermissionsKey);
    sessionStorage.removeItem(this.userRolesKey);
  }

  clearAllAuthenticationData(): void {
    this.clearRememberedLoginEmail();
    this.clearAuthenticatedSession();
  }

  checkSession(): Observable<boolean> {
    return this.apiService.post('/validate/session', {}).pipe(
      map(() => true),
      tap((status) => this.authStatus$.next(status)),
      catchError((err) => {
        this.authStatus$.next(false);
        return of(false);
      }),
    );
  }

  hasPermissions(required: string[]): boolean {
    const userPerms = this.getUserPermissions();
    return required.every((p) => userPerms.includes(p));
  }

  hasAnyPermission(required: string[]): boolean {
    const userPerms = this.getUserPermissions();
    return required.some((p) => userPerms.includes(p));
  }

  hasRoles(required: string[]): boolean {
    const userRoles = this.getUserRoles();
    return required.every((p) => userRoles.includes(p));
  }

  hasAnyRoles(required: string[]): boolean {
    const userRoles = this.getUserRoles();
    return required.some((p) => userRoles.includes(p));
  }

  clear(): void {
    sessionStorage.removeItem(this.authenticatedUserKey);
  }

  getCurrentUserRole(): string {
    return this.getUserRoles()[0] ?? '';
  }

  getCurrentUserMda(){
    const mda = sessionStorage.getItem("mdaId");
    if (!mda) return null;

    return JSON.parse(mda) as string | null;
  }
}
